'use client';

import { Check, Loader2, Minus, X } from 'lucide-react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface SelectAllToggleProps {
  /** Alle Kandidaten gewählt → Häkchen. */
  checked: boolean;
  /** Teilauswahl → Minus-Indikator (aria-checked="mixed"). */
  indeterminate?: boolean;
  /** Persistenter Umschalter: wählt alle ↔ hebt alle auf. */
  onToggle: () => void;
  /** Lädt (z. B. Belege: alle Seiten via GraphQL holen) → Spinner + deaktiviert. */
  loading?: boolean;
  disabled?: boolean;
  /** Beschriftung (Standard „Alle auswählen"). */
  label?: ReactNode;
  /** Optionaler Zähler-Suffix, z. B. <>· {n} Belege</>. */
  countSuffix?: ReactNode;
  /** Text während des Ladens (Standard „Lade …"). */
  loadingLabel?: ReactNode;
  className?: string;
  /**
   * Beendet den Auswahlmodus. Ist der Handler gesetzt, erscheint rechts neben
   * dem Toggle ein „Fertig"-Button — die zweite, sichtbare Möglichkeit den Modus
   * zu verlassen (neben dem X der SelectionBar, das viele Nutzer übersehen).
   * Erwartet dieselbe Semantik wie dieses X (üblicherweise `sel.exit`).
   */
  onExit?: () => void;
  /** Beschriftung des Beenden-Buttons (Standard „Fertig"). */
  exitLabel?: ReactNode;
  /** aria-label des Beenden-Buttons (Standard „Auswahl beenden"). */
  exitAriaLabel?: string;
}

/**
 * Geteilte „Alle auswählen"-Steuerung für die Mehrfachauswahl — einheitlich für
 * Belege, Favoriten, Projekte und Artikelsuche. Ein persistentes Toggle: solange
 * der View sie im Auswahlmodus rendert, bleibt sie sichtbar (NICHT an „noch nichts
 * gewählt" koppeln), damit man auch wieder alle abwählen kann.
 */
export function SelectAllToggle({
  checked,
  indeterminate = false,
  onToggle,
  loading = false,
  disabled = false,
  label = 'Alle auswählen',
  countSuffix,
  loadingLabel = 'Lade …',
  className,
  onExit,
  exitLabel = 'Fertig',
  exitAriaLabel = 'Auswahl beenden',
}: SelectAllToggleProps) {
  const ariaChecked: boolean | 'mixed' = indeterminate ? 'mixed' : checked;
  const filled = checked || indeterminate;

  return (
    <>
    <button
      type="button"
      role="checkbox"
      aria-checked={ariaChecked}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      onClick={onToggle}
      className={clsx(
        'flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors w-fit disabled:opacity-60',
        className,
      )}
    >
      <span
        className={clsx(
          'w-5 h-5 rounded border-2 grid place-items-center shrink-0 transition-colors',
          filled
            ? 'bg-primary border-primary text-white'
            : 'bg-white border-gray-300 text-transparent',
        )}
      >
        {loading ? (
          <Loader2 size={12} className="animate-spin text-zinc-500" />
        ) : indeterminate ? (
          <Minus size={12} strokeWidth={3} />
        ) : (
          <Check size={12} strokeWidth={3} />
        )}
      </span>
      <span className="text-sm font-medium text-gray-700">
        {loading ? (
          loadingLabel
        ) : (
          <>
            {label}
            {countSuffix ? (
              <span className="text-zinc-500 tabular-nums"> {countSuffix}</span>
            ) : null}
          </>
        )}
      </span>
    </button>
    {onExit && (
      <button
        type="button"
        onClick={onExit}
        aria-label={exitAriaLabel}
        title={exitAriaLabel}
        className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
      >
        <X size={16} />
        {exitLabel}
      </button>
    )}
    </>
  );
}
